import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

function AuthCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | confirmed | error
  const [error, setError] = useState('');
  const hasProcessed = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      if (hasProcessed.current) return;
      hasProcessed.current = true;

      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          setStatus('error');
          setError('Erro na autenticação. Tente novamente.');
          setTimeout(() => navigate('/login', { replace: true }), 3000);
          return;
        }

        if (session?.user) {
          localStorage.removeItem('orcai_pending_email');

          // Ensure profile exists in profiles table
          const { data: profile } = await supabase
            .from('profiles')
            .select('id, plano')
            .eq('user_id', session.user.id)
            .maybeSingle();

          if (!profile) {
            // Create profile if it doesn't exist
            const meta = session.user.user_metadata || {};
            await supabase.from('profiles').insert({
              user_id: session.user.id,
              nome: meta.name || meta.full_name || meta.nome || session.user.email?.split('@')[0] || '',
              email: session.user.email || '',
              whatsapp: meta.whatsapp || '',
              plano: null,
              aceita_email_marketing: meta.aceita_email_marketing || false,
            });
          }

          // Check if user has chosen a plan (from profiles table)
          const hasPlano = profile?.plano && profile.plano !== '' && profile.plano !== 'gratis';

          if (hasPlano) {
            // User already has a plan — go home
            navigate('/', { replace: true });
          } else {
            // No plan — show success screen, redirect to choose plan
            setStatus('confirmed');
            setTimeout(() => {
              navigate('/escolher-plano', { replace: true });
            }, 2500);
          }
        } else {
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 3000);
        }
      } catch {
        setStatus('error');
        setError('Erro inesperado. Redirecionando...');
        setTimeout(() => navigate('/login', { replace: true }), 3000);
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary via-secondary/95 to-primary/20 flex items-center justify-center px-4">
      <div className="bg-card rounded-2xl p-8 shadow-2xl shadow-black/20 max-w-md w-full text-center">
        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-red-600 font-medium mb-2">{error}</p>
            <p className="text-sm text-muted-foreground">Redirecionando para o login...</p>
          </>
        )}

        {status === 'confirmed' && (
          <>
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5 animate-[scale-in_0.4s_ease-out]">
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-secondary font-display mb-2">
              Email confirmado com sucesso!
            </h2>
            <p className="text-muted-foreground text-sm mb-6">
              Agora escolha seu plano para começar
            </p>
            <button
              onClick={() => navigate('/escolher-plano', { replace: true })}
              className="w-full bg-gradient-cta text-white font-bold py-3 rounded-xl hover:opacity-90 transition-all shadow-cta cursor-pointer"
            >
              Escolher meu plano →
            </button>
          </>
        )}

        {status === 'loading' && (
          <>
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <h2 className="text-lg font-bold text-secondary font-display mb-2">Autenticando...</h2>
            <p className="text-sm text-muted-foreground">Aguarde um momento</p>
          </>
        )}
      </div>
    </div>
  );
}

export default AuthCallback;
